// PaymentTable.jsx

import React, { useState, useEffect, useMemo } from 'react';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

function PaymentTable({
  people = [],
  earningsData = [],
  userPeriodData = {},
  selectedYear,
  selectedMonth,
  selectedPeriod,
  selectedNationality,
  selectedFirm,
  firms = [],
  updateNapomena,
}) {
  const [paymentData, setPaymentData] = useState([]);

   // Function to get firm name
   const getFirmName = (firmId) => {
    const firm = firms.find((f) => f.id === firmId);
    return firm ? firm.name : 'Sve';
  };

  // Filter people based on selection
  const filteredPeople = useMemo(() => {
    return people
      .filter((person) => {
        const nationalityMatch =
          selectedNationality === 'Sve' ||
          person.nationality === selectedNationality;
        const firmMatch = selectedFirm === '' || person.firmId === selectedFirm;

        const dateMatch =
          (person.startYear < selectedYear ||
            (person.startYear === selectedYear &&
              person.startMonth <= selectedMonth)) &&
          (!person.archived ||
            person.archiveYear > selectedYear ||
            (person.archiveYear === selectedYear &&
              person.archiveMonth > selectedMonth));

        return nationalityMatch && firmMatch && dateMatch;
      })
      .sort((a, b) => {
        if (selectedFirm === '') {
          // Sort by firm name first if no firm is selected, then by person ID
          const firmA = firms.find((firm) => firm.id === a.firmId)?.name || '';
          const firmB = firms.find((firm) => firm.id === b.firmId)?.name || '';
          if (firmA === firmB) {
            return a.id.localeCompare(b.id);
          }
          return firmA.localeCompare(firmB);
        } else {
          // If a firm is selected, sort only by person ID
          return a.id.localeCompare(b.id);
        }
      });
  }, [people, selectedNationality, selectedFirm, selectedYear, selectedMonth, firms]);

  useEffect(() => {
    // Function to calculate payment data
    const calculatePaymentData = () => {
      const data = filteredPeople.map((person) => {
        // Fetch period data for the person
        let periodDataList = Object.values(userPeriodData).filter(
          (data) =>
            data.userId === person.id &&
            data.year === selectedYear &&
            data.month === selectedMonth
        );

        // Adjust periodDataList based on nationality and selected period
        if (person.nationality === 'Hrvat') {
          if (selectedNationality === 'Hrvat') {
            // Only include the selected period
            periodDataList = periodDataList.filter(
              (data) => data.period === selectedPeriod
            );
          } else if (selectedNationality === 'Sve') {
            // Include both periods (1 and 2)
            periodDataList = periodDataList.filter(
              (data) => data.period === 1 || data.period === 2
            );
          }
        } else if (person.nationality === 'Stranac') {
          // For foreigners, period is always 1
          periodDataList = periodDataList.filter((data) => data.period === 1);
        }

        // Fetch earnings for the person
        let personEarnings = earningsData.filter(
          (earning) =>
            earning.userId === person.id &&
            earning.year === selectedYear &&
            earning.month === selectedMonth
        );

        // Adjust personEarnings based on nationality and selected period
        if (person.nationality === 'Hrvat') {
          if (selectedNationality === 'Hrvat') {
            // Only include the selected period
            personEarnings = personEarnings.filter(
              (earning) => earning.period === selectedPeriod
            );
          } else if (selectedNationality === 'Sve') {
            // Include both periods (1 and 2)
            personEarnings = personEarnings.filter(
              (earning) => earning.period === 1 || earning.period === 2
            );
          }
        } else if (person.nationality === 'Stranac') {
          // For foreigners, period is always 1
          personEarnings = personEarnings.filter((earning) => earning.period === 1);
        }

        // Calculate total earnings
        const zaradaTotal = personEarnings.reduce(
          (sum, earning) => sum + (parseFloat(earning.amount) || 0),
          0
        );

        
        // Function to sum deductions over periodDataList
        const getValue = (fieldName) => {
          const periodValue = periodDataList.length > 0
            ? periodDataList.reduce((sum, data) => sum + (parseFloat(data[fieldName]) || 0), 0)
            : undefined;
          
          if (periodValue !== undefined) return periodValue;
          const personValue = person[fieldName];
          return parseFloat(personValue) || 0;
        };

        // Get all financial values
        const fee = getValue('fee');
        const feeIznos = (zaradaTotal * fee) / 100;
        const total1 = zaradaTotal - feeIznos;

        // Get all deductions
        const appKnjig = getValue('appKnjig');
        const doprinosi = getValue('doprinosi');
        const renta = getValue('renta');
        const rentSmjestaj = getValue('rentSmjestaj');
        const oprema = getValue('oprema');
        const akontacija = getValue('akontacija');
        const gorivo = getValue('gorivo');
        const ostalo = getValue('ostalo');
        const bonus = getValue('bonus');

        // Get additional deductions for foreigners
        const tarifa = getValue('tarifa');
        const depozit = getValue('depozit');
        const struja = getValue('struja');
        const glovoTax = getValue('glovoTax');

        // Calculate Total 2
        let total2 =
          total1 -
          appKnjig -
          doprinosi -
          renta -
          rentSmjestaj -
          oprema -
          akontacija -
          gorivo -
          ostalo -
          (person.nationality === 'Stranac'
            ? tarifa + depozit + struja + glovoTax
            : 0) +
          bonus;

        // If Total 2 is negative, set to 0
        total2 = Math.max(0, total2);

        // **Console Logs for Debugging**
        console.log('--- Calculation for:', person.name, '---');
        console.log('Person:', person);
        console.log('Period Data List:', periodDataList);
        console.log('Earnings Data:', personEarnings);
        console.log('Zarada Total:', zaradaTotal);
        console.log('Fee (%):', fee);
        console.log('Fee Amount (Fee Iznos):', feeIznos);
        console.log('Total after Fee (Total1):', total1);
        console.log('Deductions:');
        console.log('  App Knjig:', appKnjig);
        console.log('  Doprinosi:', doprinosi);
        console.log('  Renta:', renta);
        console.log('  Rent Smjestaj:', rentSmjestaj);
        console.log('  Oprema:', oprema);
        console.log('  Akontacija:', akontacija);
        console.log('  Gorivo:', gorivo);
        console.log('  Ostalo:', ostalo);
        if (person.nationality === 'Stranac') {
          console.log('Additional Deductions for Stranac:');
          console.log('  Tarifa:', tarifa);
          console.log('  Depozit:', depozit);
          console.log('  Struja:', struja);
          console.log('  Glovo Tax:', glovoTax);
        }
        console.log('Bonus:', bonus);
        console.log('Total for Payment (Total2):', total2);
        console.log('Napomena:', periodDataList.map((data) => data.napomena || '').join('; '));
        console.log('----------------------------------------');

        return {
          name: person.name,
          totalForPayment: parseFloat(total2.toFixed(2)), // Ensure it's a number
          tekuciRacun: person.tekuciRacun || '',
          zasticeniRacun: person.zasticeniRacun || '',
          napomena: periodDataList.map((data) => data.napomena || '').join('; '),
        };
      });

      setPaymentData(data);
    };

    calculatePaymentData();
  }, [
    filteredPeople,
    earningsData,
    userPeriodData,
    selectedYear,
    selectedMonth,
    selectedPeriod,
    selectedNationality, // Added to dependencies
  ]);

  // Funkcija za izvoz u Excel s stilizacijom
  const exportToExcel = async () => {
    // Kreiramo novi workbook i dodajemo worksheet
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Konačna isplata');

    // Definiramo stupce
    worksheet.columns = [
      { header: 'Ime', key: 'name', width: 25 },
      { header: 'Total za isplatu', key: 'totalForPayment', width: 20 },
      { header: 'Tekući račun', key: 'tekuciRacun', width: 25 },
      { header: 'Zaštićeni račun', key: 'zasticeniRacun', width: 25 },
      { header: 'Napomene', key: 'napomena', width: 30 },
    ];

    // Dodajemo podatke
    paymentData.forEach((item) => {
      worksheet.addRow({
        name: item.name,
        totalForPayment: item.totalForPayment,
        tekuciRacun: item.tekuciRacun,
        zasticeniRacun: item.zasticeniRacun,
        napomena: item.napomena,
      });
    });

    // Izračunaj ukupnu sumu
    const totalSum = paymentData.reduce((sum, item) => sum + item.totalForPayment, 0);

    // Dodaj prazan red prije sume
    worksheet.addRow({});

    // Dodaj red sa sumom
    const sumRow = worksheet.addRow({
      name: 'UKUPNO:',
      totalForPayment: totalSum,
      tekuciRacun: '',
      zasticeniRacun: '',
      napomena: '',
    });

    // Stiliziramo red sa sumom
    sumRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE6E6E6' }
      };
    });

    // Formatiramo stupac 'Total za isplatu' kao broj s dvije decimale
    worksheet.getColumn('totalForPayment').numFmt = '#,##0.00';

    // Stiliziramo header redak
    worksheet.getRow(1).font = { bold: true };
    worksheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' };
    worksheet.getRow(1).height = 20;

    // Postavljamo obrube za header redak
    worksheet.getRow(1).eachCell((cell) => {
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
    });

    // Auto filter
    worksheet.autoFilter = {
      from: 'A1',
      to: 'E1',
    };

    // Stiliziramo ćelije i postavljamo obrube
    worksheet.eachRow((row, rowNumber) => {
      row.eachCell((cell, colNumber) => {
        // Postavljamo obrube za svaku ćeliju
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
        // Poravnanje
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
      });
      row.height = 20;
    });

    // Postavljamo širinu stupca 'Napomene' i omogućujemo prelamanje teksta
    worksheet.getColumn('napomena').width = 40;
    worksheet.getColumn('napomena').alignment = { wrapText: true };

    // Podebljavamo imena u stupcu 'Ime'
    worksheet.getColumn('name').eachCell((cell, rowNumber) => {
      if (rowNumber > 1 && rowNumber < worksheet.rowCount) { // Izbjegavamo zadnji red (suma)
        cell.font = { bold: true };
      }
    });

    // Spremamo workbook u buffer
    const buffer = await workbook.xlsx.writeBuffer();

    // Određujemo ime datoteke
    const firmNameForFile = selectedFirm
      ? getFirmName(selectedFirm).replace(/[^a-z0-9]/gi, '_')
      : 'sve';

    // Spremamo kao Excel datoteku
    saveAs(
      new Blob([buffer], { type: 'application/octet-stream' }),
      `Konacna_isplata_${firmNameForFile}_${selectedMonth}_${selectedYear}_P${selectedPeriod}.xlsx`
    );
  };

  return (
    <div className="payment-table-container">
      <h2>Konačna isplata</h2>
      <button onClick={exportToExcel} className="export-button">
        Izvezi u Excel
      </button>
      <table>
        <thead>
          <tr>
            <th>Ime</th>
            <th>Total za isplatu</th>
            <th>Tekući račun</th>
            <th>Zaštićeni račun</th>
            <th>Napomene</th>
          </tr>
        </thead>
        <tbody>
          {paymentData.map((item, index) => (
            <tr key={index}>
              <td><strong>{item.name}</strong></td>
              <td>{item.totalForPayment.toFixed(2)}</td>
              <td>{item.tekuciRacun}</td>
              <td>{item.zasticeniRacun}</td>
              <td>{item.napomena}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default PaymentTable;
